import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Snackbar,
  TextField,
  Button,
  Link
} from '@mui/material';
import GoogleSignInButton from './GoogleSignInButton';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

/**
 * Simplified Login Page Component
 * Clean, centered login form with email/password and Google authentication
 */
const LoginPage = () => {
  const { signInWithPassword } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // State for username/password login fields
  const [loginForm, setLoginForm] = useState({
    username: '',
    password: ''
  });

  // Handle login form changes
  const handleLoginFormChange = (e) => {
    const { name, value } = e.target;
    setLoginForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle login form submission
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError(''); // Clear any previous errors

    try {
      const { data, error } = await signInWithPassword(
        loginForm.username,
        loginForm.password
      );

      if (error) {
        console.error('Login error:', error);
        handleLoginError(error);
        return;
      }

      handleLoginSuccess();
    } catch (error) {
      console.error('Unexpected error during login:', error);
      handleLoginError(error);
    }
  };

  const handleLoginSuccess = () => {
    setShowSuccess(true);
    setTimeout(() => {
      navigate('/');
    }, 1000);
  };

  const handleLoginError = (error) => {
    setError(error.message || 'Authentication failed. Please try again.');
  };

  // Define Wyspur colors
  const wyspurColors = {
    darkNavy: '#0D0D21',
    navy: '#161C3E',
    blue: '#0095FF',
    text: '#FFFFFF'
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: wyspurColors.darkNavy,
        py: { xs: 4, md: 8 },
        px: { xs: 2, md: 4 }
      }}
    >
      {/* Centered Login Card */}
      <Paper
        elevation={3}
        sx={{
          bgcolor: wyspurColors.navy,
          borderRadius: 2,
          px: 4,
          py: 4,
          width: '100%',
          maxWidth: '450px',
          backdropFilter: 'blur(10px)'
        }}
      >
        <Typography
          variant="h4"
          sx={{
            color: wyspurColors.text,
            fontWeight: 700,
            mb: 1,
            textAlign: 'center'
          }}
        >
          Welcome Back
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: wyspurColors.text,
            opacity: 0.7,
            mb: 3,
            textAlign: 'center'
          }}
        >
          Sign in to your account
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Username/password fields */}
        <Box component="form" onSubmit={handleLoginSubmit} sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Email"
            name="username"
            type="email"
            value={loginForm.username}
            onChange={handleLoginFormChange}
            margin="normal"
            variant="filled"
            InputLabelProps={{ sx: { color: wyspurColors.text } }}
            InputProps={{
              sx: {
                color: wyspurColors.text,
                bgcolor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          />
          <TextField
            fullWidth
            label="Password"
            name="password"
            type="password"
            value={loginForm.password}
            onChange={handleLoginFormChange}
            margin="normal"
            variant="filled"
            InputLabelProps={{ sx: { color: wyspurColors.text } }}
            InputProps={{
              sx: {
                color: wyspurColors.text,
                bgcolor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          />
          <Button
            fullWidth
            type="submit"
            variant="contained"
            sx={{
              mt: 3,
              mb: 2,
              bgcolor: wyspurColors.blue,
              color: wyspurColors.text,
              fontWeight: 600,
              py: 1.5,
              '&:hover': { bgcolor: '#0077CC' }
            }}
          >
            Sign In
          </Button>
        </Box>

        <Typography
          variant="body2"
          align="center"
          sx={{ mb: 2, color: wyspurColors.text, opacity: 0.5 }}
        >
          or
        </Typography>

        <Box sx={{ mb: 3 }}>
          <GoogleSignInButton
            onSuccess={handleLoginSuccess}
            onError={handleLoginError}
          />
        </Box>

        <Typography variant="body2" sx={{ color: wyspurColors.text, mb: 2 }} align="center">
          Don't have an account?{' '}
          <Link
            href="/signup"
            sx={{
              color: wyspurColors.blue,
              fontWeight: 600,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Sign up
          </Link>
        </Typography>

        <Typography
          variant="body2"
          sx={{ color: wyspurColors.text, opacity: 0.6 }}
          align="center"
        >
          By signing in, you agree to our{' '}
          <Link href="#" sx={{ color: wyspurColors.blue }}>
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link href="#" sx={{ color: wyspurColors.blue }}>
            Privacy Policy
          </Link>
        </Typography>
      </Paper>

      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success">
          Login successful. Redirecting...
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LoginPage;
